<script setup lang="ts">
import type { RefresherCustomEvent } from '@ionic/core';

defineExpose({
  open,
  close
});

const userConfigStore = useUserConfigurationStore();
const routingHistory = useRoutingHistoryStore();
const { messageNavigated, pendingExpense } = storeToRefs(routingHistory);
const { getExpense } = useExpenseAPI();
const messageStore = useMessageStore();
const { openConfirmAlert } = useUserMessages();
const router = useRouting();
const { usingTouch } = useInputTracker();
const { t } = useEkI18n();
let scrollTarget: string | undefined = undefined;

const isOpen = ref(false);
const loading = ref(true);

const searchSubheader = computed(() => {
  return messageStore._messages.length === 0
    ? t('messages.noMessagesReceived')
    : t('messages.noSearchResults');
});

function open(messageId?: string) {
  isOpen.value = true;
  messageNavigated.value = '';
  messageStore.markAllAsRead(userConfigStore.fitterId);

  if (messageId) {
    scrollTarget = messageId;
  }
}

function scrollToMessage() {
  if (!scrollTarget) return;
  const el = document.getElementById(scrollTarget);

  if (el) {
    el.scrollIntoView();
  }
}

function close() {
  isOpen.value = false;
}

function onDismiss() {
  messageStore.searchQuery = '';

  if (router.route.hash.startsWith('#messages')) {
    useRouter().replace({ hash: '' });
  }
}

async function refresh(event?: RefresherCustomEvent) {
  loading.value = true;

  try {
    await messageStore.markAllAsRead(userConfigStore.fitterId);
    await messageStore.fetchMessages(userConfigStore.fitterId);
  } finally {
    loading.value = false;
    event?.detail.complete();
  }
}

async function openDeleteAlert(messageId: number) {
  const confirmed = await openConfirmAlert(t('alert.warning'), t('messages.deleteConfirmation'), t('buttons.delete'));

  if (confirmed) {
    await messageStore.deleteMessage(messageId);
  }
}

function goToWorksheet(message: FormattedMessage) {
  if (!message.WorksheetId || !message.Project?.Id) return;
  messageNavigated.value = useRouter().currentRoute.value.fullPath;
  router.replace(`/worksheets/${message.WorksheetId}`, true);

  close();
}

async function goToExpense(message: FormattedMessage) {
  if (!message.ExpenseId) return;

  await showLoading(t('loading.loading'));

  const response = await getExpense(message.ExpenseId);

  await hideLoading();

  if (!response || response.HasErrors || !response.Data) return;

  pendingExpense.value = response.Data;
  router.replace('/expenses', true);

  close();
}

</script>

<template>
  <Modal
    v-model:open="isOpen"
    animation-direction="vertical"
    :title="t('messages.title')"
    :close-override="close"
    @did-dismiss="onDismiss"
    @did-present="scrollToMessage"
  >
    <template #subheader>
      <IonToolbar>
        <IonSearchbar
          v-model="messageStore.searchQuery"
          class="search-bar"
          :placeholder="t('search.placeholder')"
        />
      </IonToolbar>
    </template>

    <IonContent>
      <IonRefresher
        slot="fixed"
        :disabled="!usingTouch"
        @ion-refresh="refresh"
      >
        <IonRefresherContent />
      </IonRefresher>

      <IonList v-if="messageStore.searchResults.length > 0">
        <MessagesCard
          v-for="message in messageStore.searchResults"
          :key="message.Id"
          :message="message"
          @delete="openDeleteAlert(message.Id)"
          @go-to-worksheet="goToWorksheet(message)"
          @go-to-expense="goToExpense(message)"
        />
      </IonList>

      <UserMessagesSearch
        v-else
        icon="fa-fw fa-light fa-envelope"
        :header="t('messages.noMessages')"
        :subheader="searchSubheader"
      />
    </IonContent>
  </Modal>
</template>

<style scoped>
.search-bar{
  --border-radius: 100px;
}
</style>
